import { useTranslation } from "react-i18next";
import { Box, MenuItem, Typography } from "@material-ui/core";
import SelectField from "components/Forms/SelectField";
import { Label, OptionGroup } from "components/Styled";
import { FieldSkeleton } from "components/SkeletonLoading";
import WithIconHelper from "components/WithIconHelper";
import { useRuleContext } from "pages/Rules/Form/context/ruleContext";

import { StorageNotificationTemplate } from "@dashboard-v3/api";
import useStorageNotificationTemplates from "pages/Rules/hooks/useStorageNotificationTemplates";
import { getCloudStorage as getStorage } from "pages/Rules/Form/context/ruleSelectors";

export default function StorageNotificationTemplateSelect() {
  const { state, dispatch } = useRuleContext();
  const { t } = useTranslation("rules");
  const { templates, loading } = useStorageNotificationTemplates();

  const isReplaceAttachments = state.rule.actionType === "REPLACE_ATTACHMENTS";

  if (!isReplaceAttachments) return null;

  if (loading) {
    return (
      <FieldSkeleton
        withLabel
        testId="StorageNotificationTemplateSelect__loading"
      />
    );
  }

  const storage = getStorage(state.rule);
  const currentValue = storage?.storageNotificationTemplateId ?? "";

  function handleChange(e: React.ChangeEvent<{ value: unknown }>) {
    const value = e.target.value as string;
    dispatch({
      type: "updateStep",
      payload: {
        step: "action",
        changes: {
          storageNotificationTemplateId: value || undefined,
        },
      },
    });
  }

  return (
    <OptionGroup data-testid="StorageNotificationTemplateSelect">
      <Box display="flex" alignItems="center" mb={0.8}>
        <Label
          wording={t("storageNotificationTemplate.label", {
            defaultValue: "Template for link and notification messages",
          })}
          gutterBottom={false}
        />
        <WithIconHelper
          position="before"
          text={
            <>
              <Typography variant="subtitle2" gutterBottom>
                {t("storageNotificationTemplate.helpTitle", {
                  defaultValue: "What does this template do?",
                })}
              </Typography>
              <Typography variant="body2">
                {t("storageNotificationTemplate.helpBody", {
                  defaultValue:
                    "Emails include messages associated with shared links. Select a template to customize those texts. If none is selected, the platform default messages will be applied automatically.",
                })}
              </Typography>
            </>
          }
          buttonStyles={{ marginLeft: 4 }}
        />
      </Box>
      <SelectField<StorageNotificationTemplate>
        name="storageNotificationTemplateId"
        label=""
        allowEmpty
        emptyOption={t("storageNotificationTemplate.empty", {
          defaultValue: "Use platform default messages",
        })}
        options={templates}
        value={currentValue}
        onChange={handleChange}
        renderValue={val => {
          const found = templates.find(o => o.id === val);
          return found?.name ?? "";
        }}
        renderOption={opt => (
          <MenuItem key={opt.id} value={opt.id}>
            <Typography>{opt.name}</Typography>
          </MenuItem>
        )}
      />
      <Box mt={1}>
        <Typography variant="caption" color="textSecondary">
          {t("storageNotificationTemplate.helperText", {
            defaultValue:
              "Choose a template to customize messages in emails; leave it empty to keep the platform defaults.",
          })}
        </Typography>
      </Box>
    </OptionGroup>
  );
}
